// src/components/messaging/SenderBanners.tsx
"use client";

import React from 'react';
import { titleFor, descriptionFor, type PolicyCode } from './strings';

export function LiveAnnouncer({ message, mode = 'polite' }: { message?: string; mode?: 'polite'|'assertive' }) {
  return (
    <div aria-live={mode} className="sr-only" data-testid="live-announcer">
      {message || ''}
    </div>
  );
}

export function PolicyBanner({
  code,
  hours,
  onOpenProfile,
  onClose,
}: {
  code: PolicyCode;
  hours?: number;
  onOpenProfile?: () => void;
  onClose?: () => void;
}) {
  const title = titleFor(code);
  const desc = descriptionFor(code, { hours });
  const showProfileCta = code === 'friends_only' || code === 'post_only';
  const ref = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    try { ref.current?.focus(); } catch {}
  }, []);

  return (
    <div ref={ref} tabIndex={-1} role="alert" className="rounded-md border border-border bg-amber-50 dark:bg-amber-900/10 text-foreground p-3 flex items-start gap-3" aria-live="assertive">
      <div className="flex-1 min-w-0">
        <div className="font-semibold" data-testid="banner.title">{title}</div>
        {desc && <div className="text-sm opacity-90" data-testid="banner.desc">{desc}</div>}
        {showProfileCta && (
          <div className="mt-2">
            <button
              type="button"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border bg-background hover:bg-muted"
              onClick={onOpenProfile}
              aria-label={code === 'friends_only' ? 'Open profile to add friend' : 'Open profile to reply from a post'}
              data-testid="banner.cta"
            >
              Open profile
            </button>
          </div>
        )}
      </div>
      <button
        type="button"
        className="shrink-0 px-2 py-1 rounded-md hover:bg-muted"
        aria-label="Close notification"
        onClick={onClose}
        data-testid="banner.close"
      >
        ×
      </button>
    </div>
  );
}

export default PolicyBanner;
